import { CommandProps, Editor } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import tippy, { GetReferenceClientRect, Instance } from 'tippy.js';

import { P, Separator, legacyTextStyles } from '@eluve/components';

import { MenuGroup } from './slash-command';

const MAX_VISIBLE = 3;
export interface CommandItemProps {
  title: string;
  description: string;
  group: MenuGroup;
  icon?: ReactNode;
  command: (props: CommandProps) => void;
}

interface CommandSection {
  isExpanded?: boolean;
  header: string;
  visibleItems: CommandItemProps[];
  hiddenCount: number;
}

export const updateScrollView = (container: HTMLElement, item: HTMLElement) => {
  const containerHeight = container.clientHeight;
  const maxScrollTop = container.scrollHeight - containerHeight;
  const itemTop = item.offsetTop;
  const itemHeight = item.offsetHeight;
  // Calculate target scrollTop to roughly center the item.
  let target = itemTop - containerHeight / 2 + itemHeight / 2;
  target = Math.max(0, Math.min(target, maxScrollTop));
  container.scrollTo({ top: target, behavior: 'smooth' });
};

/**
 * Groups items by the group key.
 */
function groupItems(
  items: CommandItemProps[],
): Record<string, CommandItemProps[]> {
  return items.reduce(
    (acc, item) => {
      const key = item.group;
      acc[key] ??= [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, CommandItemProps[]>,
  );
}

/**
 * The CommandList component renders suggestions in grouped sections.
 * Each group will only show up to MAX_VISIBLE items, and if there are more,
 * a "#hidden more results" label is rendered below the group.
 * Only the visible items are added to the flat list used for keyboard navigation.
 */
const CommandList = ({
  items,
  command,
}: {
  items: CommandItemProps[];
  command: (item: CommandItemProps) => void;
}) => {
  const grouped = groupItems(items);

  const sections: CommandSection[] = [];

  // Track which groups are expanded (keyed by group name)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );

  if (grouped[MenuGroup.STYLING] && grouped[MenuGroup.STYLING].length > 0) {
    const stylingItems = grouped[MenuGroup.STYLING];
    sections.push({
      header: 'Styling',
      visibleItems: stylingItems,
      hiddenCount: 0, // Do not show "more" for styling items
    });
  }
  if (grouped[MenuGroup.SNIPPET] && grouped[MenuGroup.SNIPPET].length > 0) {
    const snippetItems = grouped[MenuGroup.SNIPPET];
    const hiddenCount =
      snippetItems.length - snippetItems.slice(0, MAX_VISIBLE)?.length;
    sections.push({
      header: 'Snippet',
      visibleItems: snippetItems,
      hiddenCount,
    });
  }

  // For each section, if expandedGroups[group] is true then show all, otherwise show limited list.
  const displaySections: CommandSection[] = sections.map((section) => {
    const isExpanded = expandedGroups[section.header] ?? false;
    const visibleItems =
      isExpanded || section.hiddenCount === 0
        ? section.visibleItems
        : section.visibleItems.slice(0, MAX_VISIBLE);
    return { ...section, visibleItems, isExpanded };
  });

  // Create a flat list of only the selectable items (i.e. visible items only).
  const flatItems = displaySections.reduce(
    (acc, sec) => acc.concat(sec.visibleItems),
    [] as CommandItemProps[],
  );

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const commandListContainer = useRef<HTMLDivElement>(null);

  const selectItem = useCallback(
    (index: number) => {
      const item = flatItems[index];
      if (item) {
        command(item);
      }
    },
    [flatItems, command],
  );

  useEffect(() => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter'];
    const onKeyDown = (e: KeyboardEvent) => {
      if (navigationKeys.includes(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowUp') {
          setSelectedIndex((selectedIndex + items.length - 1) % items.length);
          return true;
        }
        if (e.key === 'ArrowDown') {
          setSelectedIndex((selectedIndex + 1) % items.length);
          return true;
        }
        if (e.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [flatItems, selectedIndex, selectItem, items.length]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useEffect(() => {
    if (commandListContainer.current) {
      const container = commandListContainer.current;
      const item = container.children[selectedIndex] as HTMLElement;
      if (item && container) {
        updateScrollView(container, item);
      }
    }
  }, [selectedIndex]);

  return (
    flatItems.length > 0 && (
      <div
        id="slash-command"
        ref={commandListContainer}
        className="z-50 h-auto max-h-[450px] w-[340px] overflow-y-auto rounded-md border border-border-primary bg-white p-3 shadow-xl transition-all"
      >
        {displaySections.map((section, sectionIndex: number) => (
          <div key={sectionIndex}>
            <P
              className={legacyTextStyles.body({
                size: 's',
                weight: 'bold',
                color: 'supporting',
                className: 'px-2 py-1 text-sm uppercase',
              })}
            >
              {section.header}
            </P>
            {section.visibleItems.map((item, itemIndex) => {
              // Get flat index = sum of visible counts from previous sections plus current index.
              const flatIndex =
                sections
                  .slice(0, sectionIndex)
                  .reduce((acc, sec) => acc + sec.visibleItems.length, 0) +
                itemIndex;

              return (
                <button
                  key={`${sectionIndex}-${itemIndex}`}
                  onClick={() => selectItem(flatIndex)}
                  className={`flex w-full items-center space-x-2 rounded-md px-4 py-1 text-left text-sm text-text-primary hover:bg-bg-tertiary active:bg-border-primary ${
                    flatIndex === selectedIndex
                      ? 'bg-bg-tertiary text-text-primary'
                      : ''
                  }`}
                >
                  {item.icon && (
                    <div className="flex size-10 items-center justify-center rounded-md border border-border-primary bg-white">
                      {item.icon}
                    </div>
                  )}
                  <div>
                    <p className="max-w-[200px] truncate font-semibold leading-snug">
                      {item.title}
                    </p>
                    <p className="max-w-[260px] truncate py-1 text-xs leading-snug text-text-tertiary">
                      {item?.description}
                    </p>
                  </div>
                </button>
              );
            })}
            {!section.isExpanded && section.hiddenCount > 0 && (
              <button
                onClick={() =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [section.header]: true,
                  }))
                }
                className="px-2 py-1 text-sm font-medium text-text-supporting"
              >
                {section.hiddenCount} more results
              </button>
            )}
            {sectionIndex !== displaySections.length - 1 && (
              <Separator className="border border-border-primary" />
            )}
          </div>
        ))}
      </div>
    )
  );
};

export const renderItems = () => {
  let component: ReactRenderer | null = null;
  let popup: Instance[] | null = null;
  return {
    onStart: (props: {
      editor: Editor;
      clientRect: GetReferenceClientRect;
      items: CommandItemProps[];
      command: (item: CommandItemProps) => void;
    }) => {
      component = new ReactRenderer(CommandList, {
        props,
        editor: props.editor,
      });
      popup = tippy('body', {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
      });
    },
    onUpdate: (props: {
      editor: Editor;
      clientRect: GetReferenceClientRect;
    }) => {
      component?.updateProps(props);
      if (popup?.[0]) {
        popup[0].setProps({
          getReferenceClientRect: props.clientRect,
        });
      }
    },
    onKeyDown: (props: { event: KeyboardEvent }) => {
      if (props.event.key === 'Escape') {
        popup?.[0]?.hide();
        return true;
      }
      return (
        component?.ref as { onKeyDown: (props: unknown) => void }
      )?.onKeyDown(props);
    },
    onExit: () => {
      popup?.[0]?.destroy();
      component?.destroy();
    },
  };
};
